/**
 * The builder's preview: the draft run on one workspace's real data (or all
 * of them), drawn by the same LookerTileGrid /looker uses. Nothing is saved;
 * the server runs every tile through scopeQuery. A box /looker would hide
 * while it is empty is drawn here with that note, so it never just vanishes.
 *
 * @module components/looker/admin/builder/BuilderPreview
 */
import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { LookerTileGrid } from '@/components/looker/LookerTileGrid';
import { useLookerAppTiles } from '@/hooks/useLookerAppTiles';
import type { AdminWorkspace } from '@/services/adminService';
import { defaultLayout } from '@/services/looker/lookerLayout';
import type { LookerDashboardPayload } from '@/services/looker/lookerTypes';

/** A workspace id, or 'all' for every workspace. */
export type PreviewTarget = string;

interface Props {
  workspaces: readonly AdminWorkspace[];
  target: PreviewTarget;
  onTarget: (target: PreviewTarget) => void;
  onPreview: () => void;
  /** The draft cannot be previewed while it is invalid. */
  disabled: boolean;
  busy: boolean;
  payload: LookerDashboardPayload | null;
  error: string | null;
}

const BUTTON =
  'inline-flex h-8 items-center gap-1 rounded-md border border-neutral-300 bg-white px-3 text-sm font-medium text-ink-900 hover:bg-neutral-100 disabled:cursor-not-allowed disabled:opacity-50';

const noop = () => undefined;

export const BuilderPreview: React.FC<Props> = ({ workspaces, target, onTarget, onPreview, disabled, busy, payload, error }) => {
  const { t } = useTranslation();
  const id = useId();
  // The same app-box loads /looker runs: one workspace draws them, all workspaces asks for one.
  const appStates = useLookerAppTiles(payload);
  return (
    <section className="space-y-3" aria-label={t('looker.builder.preview')}>
      <h2 className="text-sm font-semibold text-ink-900">{t('looker.builder.preview')}</h2>
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-0 flex-1">
          <label className="block text-sm font-medium text-ink-900" htmlFor={`${id}-ws`}>{t('looker.builder.previewWorkspace')}</label>
          <select id={`${id}-ws`} className="w-full rounded-md border border-neutral-300 px-2 py-1 text-sm" value={target}
            onChange={(e) => onTarget(e.target.value)}>
            <option value="all">{t('looker.builder.allWorkspaces')}</option>
            {workspaces.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </div>
        <button type="button" className={BUTTON} disabled={disabled || busy} onClick={onPreview}>
          {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
          {t('looker.builder.previewButton')}
        </button>
      </div>
      {disabled && <p className="text-xs text-neutral-800">{t('looker.builder.previewFixFirst')}</p>}
      {error && <p role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>}
      {payload ? (
        <LookerTileGrid tiles={payload.tiles} layout={defaultLayout(payload.tiles)} editing={false} showHiddenNote onChange={noop} appStates={appStates} />
      ) : (
        !error && <p className="text-sm text-neutral-800">{t('looker.builder.previewEmpty')}</p>
      )}
    </section>
  );
};
